import type { Dispatch, SetStateAction } from 'react';
import { showAlert } from '../../../utils/telegram/alert';
import { useTranslation } from 'react-i18next';
import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFunction } from 'react-router-dom';
import type { useCreateUserAction } from '../../../hooks/useCreateUserAction';
import type { DailyQuestAssignAndGetResponse, DailyQuestWithProgress } from '../../../models/apiTypes';
import type { DailyQuestStartTarget, DailyQuestView } from '../../../models/quest';
import { CHAT_URL } from '../../../config';
import { isAdQuest } from '../../../utils/quest/questHelpers';
import { markAdQuestFinishedLocally } from '../../../utils/quest/optimisticQuestState';
import { taskAdLog } from '../../../utils/quest/taskAdDebugLog';
import posthog from 'posthog-js';
import type { UseDailyQuestsRefetchResult } from './useDailyQuestsRefetch';

/**
 * Marks an ad quest finished in the query cache RIGHT NOW, synchronously —
 * not "eventually, once a refetch lands". markAdQuestFinishedLocally() alone
 * only patches FUTURE cache writes (see preserveOptimisticQuestState); it does
 * nothing to data already sitting in the cache, so a component reading that
 * cache right after would still see is_finished: false until some other
 * network response happens to arrive. If a navigation away (or the whole page
 * tearing down) interrupts before that response lands, the UI never reflects
 * completion at all. Patching the three quest-list cache entries directly
 * here is what makes it actually immediate.
 */
function patchAdQuestFinishedInCache(queryClient: QueryClient, userQuestId: number): void {
  markAdQuestFinishedLocally(userQuestId);

  const questKeys = ['dailyQuests', 'hourlyQuests', 'marketingQuests'] as const;
  questKeys.forEach((key) => {
    const currentData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>([key]);
    if (!currentData?.quests) return;

    const updatedQuests = currentData.quests.map((q: DailyQuestWithProgress) => {
      if (q.user_quest_id !== userQuestId) return q;

      const adObjective = q.objectives?.find(
        (obj) => obj.type === 'watch_ad' || obj.type === 'watch_ads'
      );
      if (!adObjective) return q;

      const updatedObjectives = q.objectives?.map((obj) =>
        obj.objective_id === adObjective.objective_id
          ? { ...obj, current_count: obj.target_count, is_completed: true }
          : obj
      );
      const allObjectivesCompleted =
        updatedObjectives?.every((obj) => obj.is_completed && obj.current_count >= obj.target_count) ?? false;

      return { ...q, objectives: updatedObjectives, is_finished: allObjectivesCompleted };
    });

    queryClient.setQueryData<DailyQuestAssignAndGetResponse>([key], {
      ...currentData,
      quests: updatedQuests,
    });
  });
}

export interface UseQuestActionsParams {
  queryClient: QueryClient;
  optimisticallyCompletedAdQuestsRef: { current: Set<number> };
  refetchAllQuests: UseDailyQuestsRefetchResult['refetchAllQuests'];
  showAdFromAdsGram: (onReward: () => void) => Promise<void>;
  setShowingAdQuestIds: Dispatch<SetStateAction<Set<number>>>;
  createUserAction: ReturnType<typeof useCreateUserAction>['createUserAction'];
  onClose: () => void;
  navigate: NavigateFunction;
  setStartedVerificationQuestIds: Dispatch<SetStateAction<Set<number>>>;
  setCloudStorageItem: (key: string, value: string, callback?: (success: boolean) => void) => void;
  questsWithoutSortingRef: { current: DailyQuestView[] };
}

export interface UseQuestActionsResult {
  needsStartButton: (quest: DailyQuestStartTarget) => boolean;
  handleStartAction: (quest: Pick<DailyQuestView, 'questId' | 'userQuestId' | 'objectives'>) => Promise<void>;
  handleTaskAdReward: () => Promise<void>;
}

export function useQuestActions({
  queryClient,
  optimisticallyCompletedAdQuestsRef,
  refetchAllQuests,
  showAdFromAdsGram,
  setShowingAdQuestIds,
  createUserAction,
  onClose,
  navigate,
  setStartedVerificationQuestIds,
  setCloudStorageItem,
  questsWithoutSortingRef,
}: UseQuestActionsParams): UseQuestActionsResult {
  const { t } = useTranslation();
  // Перевірка, чи потрібна кнопка START для квесту
  const needsStartButton = (quest: DailyQuestStartTarget): boolean => {
    if (quest.isFinished) return false;
    
    const objectiveType = quest.objectives?.[0]?.type;
    const questIsAdQuest = isAdQuest(quest.questId, objectiveType);
    
    return quest.questId === 5 || quest.questId === 6 || quest.questId === 7 || quest.questId === 106 || quest.questId === 107 || quest.questId === 108 || questIsAdQuest;
  };

  // Уніфікована функція для обробки кнопки START для різних типів квестів
  const handleStartAction = async (quest: Pick<DailyQuestView, 'questId' | 'userQuestId' | 'objectives'>) => {
    const objectiveType = quest.objectives?.[0]?.type;
    const questIsAdQuest = isAdQuest(quest.questId, objectiveType);

    // Обробка квестів з рекламою
    if (questIsAdQuest) {
      if (!quest.userQuestId) {
        // Не показуємо alert - adsgram має свої власні обробники помилок
        return;
      }

      const userQuestId = quest.userQuestId;
      let adRewardDelivered = false;

      posthog.capture('ad_quest_view_started', {
        quest_id: quest.questId,
        user_quest_id: userQuestId,
        objective_type: objectiveType,
      });

      // Callback для запуску progress bar після завершення реклами
      const completeAdQuestLocally = () => {
        patchAdQuestFinishedInCache(queryClient, userQuestId);
        optimisticallyCompletedAdQuestsRef.current.add(userQuestId);

        // Refetch щоб підхопити зміни від бекенду
        refetchAllQuests().catch((error) => {
          console.error("[DailyQuestsModal] Failed to refetch after ad completion:", error);
        });
      };
      
      // Блокуємо кнопку під час показу реклами
      setShowingAdQuestIds(prev => new Set(prev).add(userQuestId));
      let didHideTelegramBackButton = false;
      
      try {
        // Quest 8 uses a frontend action rather than the Task Ad S2S callback.
        // Keep the BackButton hidden until both the ad and its backend action finish.
        if (quest.questId === 8) {
          // Під час перегляду реклами ховаємо Telegram BackButton, щоб уникнути
          // випадкового виходу користувача з квестового флоу.
          try {
            window.Telegram?.WebApp?.BackButton?.hide?.();
            didHideTelegramBackButton = true;
          } catch {
            // no-op
          }
        }

        // The claim button unlocks the moment the ad closes: not earlier, so it
        // cannot be reached without watching, and not after a backend round
        // trip, which would only make the player wait.
        await showAdFromAdsGram(() => {
          adRewardDelivered = true;
        });

        completeAdQuestLocally();

        if (quest.questId === 8) {
          // Recorded in the background; the player can already claim.
          void createUserAction('watch_ad_interstitial_26097').catch((error) => {
            console.error("[DailyQuestsModal] Failed to record ad action:", error);
          });
        }
        
        posthog.capture('ad_quest_view_result', {
          quest_id: quest.questId,
          user_quest_id: userQuestId,
          objective_type: objectiveType,
          result: 'success',
          reward_delivered: adRewardDelivered,
        });

        // Одразу оновлюємо дані через refetch в фоні (без блокування UI)
        refetchAllQuests().catch((error) => {
          console.error("[DailyQuestsModal] Failed to refetch after ad:", error);
        });
      } catch (error) {
        posthog.capture('ad_quest_view_result', {
          quest_id: quest.questId,
          user_quest_id: userQuestId,
          objective_type: objectiveType,
          result: 'error',
          error_message: error instanceof Error ? error.message : String(error),
        });
        // This used to be dead silence: the button dimmed, the Telegram
        // BackButton stayed hidden, and the player got no explanation at all.
        const reason = error instanceof Error ? error.message : String(error);
        console.error("[DailyQuestsModal] AdsGram failed:", error);
        showAlert(t("Ads not available: {{reason}}", { reason }));
      } finally {
        if (quest.questId === 8 && didHideTelegramBackButton) {
          try {
            window.Telegram?.WebApp?.BackButton?.show?.();
          } catch {
            // no-op
          }
        }

        // Розблоковуємо кнопку після завершення (успішного або з помилкою)
        setShowingAdQuestIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(userQuestId);
          return newSet;
        });
      }
      
      return;
    }

    // Обробка інших типів квестів
    switch (quest.questId) {
      case 5: // Shell hunt - перехід на екран тапу (100 разів)
      case 106: // Expert Pearl Hunter - перехід на екран тапу (1000 разів)
      case 107: // Master Pearl Hunter - перехід на екран тапу (10000 разів)
        onClose();
        navigate('/tap');
        break;
      
      case 6: // Join Community Group - відкриття Telegram групи
        window.open('https://t.me/web3islands', '_blank');
        // Позначаємо, що кнопка START була натиснута - показуємо "Перевірити"
        if (quest.userQuestId) {
          setStartedVerificationQuestIds(prev => new Set(prev).add(quest.userQuestId!));
          // Зберігаємо в CloudStorage
          setCloudStorageItem(`quest_6_started`, 'true');
        }
        break;
      
      case 7: // Partner miniapp - відкриття TON Faucet
        window.open('https://t.me/TonFaucetCryptoBot/faucet?startapp=web3islands', '_blank');
        // Позначаємо, що кнопка START була натиснута - показуємо "Перевірити"
        if (quest.userQuestId) {
          setStartedVerificationQuestIds(prev => new Set(prev).add(quest.userQuestId!));
          // Зберігаємо в CloudStorage
          setCloudStorageItem(`quest_7_started`, 'true');
        }
        break;

      case 108: // Join Community English Chat - відкриття Telegram чату
        window.open(CHAT_URL, '_blank');
        // Позначаємо, що кнопка START була натиснута - показуємо "Перевірити"
        if (quest.userQuestId) {
          setStartedVerificationQuestIds(prev => new Set(prev).add(quest.userQuestId!));
          // Зберігаємо в CloudStorage
          setCloudStorageItem(`quest_108_started`, 'true');
        }
        break;
      
      case 172: // Write to bot - відкриття Telegram бота
        window.open('https://t.me/web3islands_bot?start=quest_send_message_to_bot', '_blank');
        break;

      default:
        // Unknown quest type - do nothing
    }
  };

  // Обробник нагороди від task ad (AdsGram Task web component)
  const handleTaskAdReward = async () => {
    taskAdLog('handleTaskAdRewardEntered', {});
    const quest41 = questsWithoutSortingRef.current.find(q => q.questId === 41);
    if (!quest41?.userQuestId) {
      taskAdLog('handleTaskAdReward_quest41NotFound', {});
      console.error('[TaskAd] Quest 41 not found');
      return;
    }
    const userQuestId = quest41.userQuestId;
    taskAdLog('handleTaskAdReward_beforePatch', { userQuestId, wasFinished: quest41.isFinished });

    // The GO tap unlocks the gift right now — synchronously, in the query
    // cache, not "once a refetch happens to land". The earlier version only
    // called markAdQuestFinishedLocally() (which patches FUTURE cache writes)
    // and awaited refetchAllQuests() to make it visible; if navigating away —
    // to another Mini App, or a bot chat that tears the whole page down —
    // interrupted before that network round trip completed, the UI never
    // reflected completion at all, which is exactly the reported symptom.
    patchAdQuestFinishedInCache(queryClient, userQuestId);
    optimisticallyCompletedAdQuestsRef.current.add(userQuestId);
    taskAdLog('handleTaskAdReward_afterPatch', {});

    // Recorded in the background; claiming no longer waits for it.
    void createUserAction('watch_ad_task_26003')
      .then(() => taskAdLog('createUserActionOk', {}))
      .catch((error) => {
        taskAdLog('createUserActionFailed', { message: error instanceof Error ? error.message : String(error) });
        console.error('[TaskAd] Failed to record task ad action:', error);
      });

    try {
      await refetchAllQuests();
      taskAdLog('refetchLanded', {});
    } catch (error: unknown) {
      taskAdLog('refetchFailed', { message: error instanceof Error ? error.message : String(error) });
      console.error('[TaskAd] Failed to confirm quest completion:', error);
    }
  };

  return {
    needsStartButton,
    handleStartAction,
    handleTaskAdReward,
  };
}
